import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { DviView } from '../dvi/DviView';
import { FragmentView } from '../dvi/FragmentView';
import { countInlineMath, MathText } from '../dvi/MathText';
import { findArticle, findChild } from '../data';
import type { ArticleHit } from '../data';
import { dateLabel } from '../lib/wareki';
import { ChildPage } from './ChildPage';
import { NotFound } from './NotFound';
import { ErrorBoundary } from '../components/ErrorBoundary';

/**
 * /<category>/:key のディスパッチャ。key が子要素なら子要素トップページ、
 * さもなくば記事 (section = カテゴリ key の /math/:slug 等) として解決する。
 */
export function CategoryEntry({ categoryKey }: { categoryKey: string }) {
  const { key } = useParams<{ key: string }>();
  const childHit = findChild(key ?? '');
  if (childHit && childHit.category.key === categoryKey) {
    return <ChildPage category={childHit.category} child={childHit.child} />;
  }
  const articleHit = findArticle(categoryKey, key ?? '');
  return articleHit ? <ArticlePage hit={articleHit} /> : <NotFound />;
}

/** 旧 URL 空間 (/logic/:slug) の記事ルート。URL 互換のため残している。 */
export function LegacyArticle({ section }: { section: string }) {
  const { slug } = useParams<{ slug: string }>();
  const hit = findArticle(section, slug ?? '');
  return hit ? <ArticlePage hit={hit} /> : <NotFound />;
}

/**
 * 記事ページ本体。DVI 記事は DviView でインライン表示
 * (紙面レイアウト + fit-width + 遅延描画)。PDF 記事はリンクを提示する。
 */
export function ArticlePage({ hit }: { hit: ArticleHit }) {
  const { category, child, article } = hit;
  const link = article.link;
  const isDvi = link !== null && /\.dvi$/i.test(link.url);
  // 紙面 DVI のビューワ: ページめくり (幅が広ければ 3 ページ見開き) と
  // 従来のスクロールを切り替えられる。複数ページのときだけ意味を持つ
  const multiPage = (article.dvi?.pages ?? 0) > 1;
  const [viewerMode, setViewerMode] = useState<'paged' | 'scroll'>('paged');

  // \ref のサイト内リンク (…#<stem>) でその断片へスクロールする。React Router の
  // Link はハッシュへ自動スクロールしないため補う。断片スケルトンは事前計測した
  // 高さを確保するのでレイアウトは安定しており初回でほぼ合うが、DVI/フォント確定後
  // の微調整として少し後に一度だけ再スクロールする。
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      return;
    }
    const id = decodeURIComponent(hash.slice(1));
    let active = true;
    const scroll = () => {
      if (active) {
        document.getElementById(id)?.scrollIntoView({ block: 'start' });
      }
    };
    scroll();
    // 上方の断片が遅れて frag.json を読み込み高さが増えるとターゲットが下へ
    // 押し下げられるため、レイアウトが確定するまで追従して再スクロールする。
    const ro = new ResizeObserver(scroll);
    ro.observe(document.body);
    const stop = window.setTimeout(() => {
      active = false;
      ro.disconnect();
    }, 3000);
    return () => {
      active = false;
      ro.disconnect();
      clearTimeout(stop);
    };
  }, [hash, article.slug]);

  // ビューワカードの幅はスクロール量に連動する連続量 --dvi-expand (0〜1) で
  // 制御する。カード上端がビューポート下半分にあるうちは 0 (本文カラム幅)、
  // 上端 10% に達すると 1 (拡幅・見開き)。smoothstep でイージングし、上へ
  // 戻せば同じ経路で滑らかに元へ戻る。進捗はカード上端の位置だけから導く
  // (カード自身の高さ変化に依存させない。フィードバック振動を避けるため)
  const viewerRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = viewerRef.current;
    if (!el) {
      return;
    }
    if (!multiPage || viewerMode !== 'paged') {
      el.style.setProperty('--dvi-expand', '0');
      return;
    }
    let raf = 0;
    const update = () => {
      raf = 0;
      const vh = window.innerHeight;
      // 見開きに足る幅が無いビューポート (拡幅後スロット幅 < 300px 相当)
      // では常に折り畳み
      const wideInner = Math.min(window.innerWidth * 0.94, 1500) - 40;
      let p = 0;
      if (vh > 0 && wideInner >= 900) {
        const top = el.getBoundingClientRect().top;
        const start = vh * 0.5;
        const end = vh * 0.1;
        const raw = Math.min(1, Math.max(0, (start - top) / (start - end)));
        p = raw * raw * (3 - 2 * raw); // smoothstep
      }
      el.style.setProperty('--dvi-expand', String(p));
    };
    const onScroll = () => {
      if (!raf) {
        raf = requestAnimationFrame(update);
      }
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) {
        cancelAnimationFrame(raf);
      }
    };
  }, [multiPage, viewerMode]);

  return (
    <>
      <div className="hero">
        <p className="article-breadcrumb">
          <Link to={`/${category.key}`}>{category.title}</Link> /{' '}
          <Link to={`/${category.key}/${child.key}`}>{child.title}</Link>
          {article.group && (
            <> / {child.groups?.find((g) => g.key === article.group)?.title ?? article.group}</>
          )}
        </p>
        <h1>
          <MathText
            text={article.title}
            formulasUrl={`/articles/${article.slug}/meta.formulas.dvi`}
            pages={article.metaPages}
          />
        </h1>
        <p className="article-meta">
          公開日: {dateLabel(article.status, article.published)} ・ 更新日:{' '}
          {dateLabel(article.status, article.updated)}
        </p>
      </div>
      <section className="card">
        <h2>概要</h2>
        <p>
          <MathText
            text={article.abstract}
            formulasUrl={`/articles/${article.slug}/meta.formulas.dvi`}
            pages={article.metaPages}
            pageOffset={countInlineMath(article.title)}
          />
        </p>
      </section>
      {article.fragments && (
        <section className="card">
          {article.fragments.map((stem) => (
            <div className="bourbaki-fragment" id={stem} key={stem}>
              <ErrorBoundary label={stem}>
                <FragmentView
                  src={`/articles/${article.slug}/${stem}`}
                  draft={article.draftFragments?.includes(stem)}
                />
              </ErrorBoundary>
            </div>
          ))}
        </section>
      )}
      {isDvi && link && (
        <section className="card card-dvi" ref={viewerRef}>
          <div className="article-body-header">
            <h2>本文</h2>
            {multiPage && (
              <div className="dvi-viewer-toggle" role="group" aria-label="表示方法">
                <button
                  className={viewerMode === 'paged' ? 'active' : undefined}
                  onClick={() => setViewerMode('paged')}
                >
                  ページめくり
                </button>
                <button
                  className={viewerMode === 'scroll' ? 'active' : undefined}
                  onClick={() => setViewerMode('scroll')}
                >
                  スクロール
                </button>
              </div>
            )}
          </div>
          <DviView
            src={link.url}
            scaling="fit-width"
            pager={multiPage ? viewerMode : 'scroll'}
            frame
            background="paper"
            sizeHint={article.dvi ?? undefined}
            urlParam="page"
          />
          <p className="article-download">
            <a href={link.url} download>
              DVI 原本をダウンロード
            </a>
          </p>
        </section>
      )}
      {!isDvi && link && (
        <section className="card">
          <h2>本文</h2>
          <p>
            <a href={link.url} target="_blank" rel="noreferrer">
              {link.label} で開く
            </a>
          </p>
        </section>
      )}
      {!link && !article.fragments && (
        <section className="card">
          <h2>本文</h2>
          <p>準備中です。</p>
        </section>
      )}
    </>
  );
}
